import { Cell as CellType } from '@convergence/types';
import { observer } from 'mobx-react';
import { useRef } from 'react';
import { useDrag, useDrop, XYCoord } from 'react-dnd';
import { toast } from 'react-toastify';
import { MarkdownOrCode, NotebookStore } from '../stores/NotebookStore';
import { CodeCell } from './CodeCell';
import { MarkdownCell } from './MarkdownCell';
import { CellDivider } from './Notebook';
import { ArrowDownIcon, ArrowUpIcon, TrashIcon } from '@phosphor-icons/react';
import { Identifier } from 'dnd-core';

interface CellProps {
  cell: CellType;
  store: NotebookStore;
  index: number;
  readonly: boolean;
  activeCellId: string | null;
}

interface DragItem {
  index: number;
  id: string;
  type: string;
}

const ItemTypes = {
  CELL: 'cell'
};

// const resultStyles = cva(['px-4', 'py-2', 'bg-white', 'rounded'], {
//   variants: {
//     result: {
//       success: ['text-green-600'],
//       error: ['text-red-600']
//     }
//   }
// });

export const Cell = observer(
  ({ cell, store, index, readonly, activeCellId }: CellProps) => {
    const ref = useRef<HTMLDivElement>(null);

    const [{ handlerId }, drop] = useDrop<
      DragItem,
      void,
      { handlerId: Identifier | null }
    >({
      accept: ItemTypes.CELL,
      collect(monitor) {
        return {
          handlerId: monitor.getHandlerId()
        };
      },
      hover(item: DragItem, monitor) {
        if (!ref.current) {
          return;
        }
        const dragIndex = item.index;
        const hoverIndex = index;
        if (dragIndex === hoverIndex) {
          return;
        }
        const hoverBoundingRect = ref.current?.getBoundingClientRect();
        const hoverMiddleY =
          (hoverBoundingRect.bottom - hoverBoundingRect.top) / 2;
        const clientOffset = monitor.getClientOffset();
        const hoverClientY =
          (clientOffset as XYCoord).y - hoverBoundingRect.top;

        if (dragIndex < hoverIndex && hoverClientY < hoverMiddleY) {
          return;
        }
        if (dragIndex > hoverIndex && hoverClientY > hoverMiddleY) {
          return;
        }
        store.moveCell(dragIndex, hoverIndex);
        item.index = hoverIndex;
      }
    });

    const [{ isDragging }, drag] = useDrag(() => ({
      type: ItemTypes.CELL,
      item: () => {
        return { id: cell.id, index };
      },
      collect: (monitor) => ({
        isDragging: !!monitor.isDragging()
      })
    }));

    drag(drop(ref));

    if (cell.cell_type === 'code') {
      return (
        <>
          <CodeCell
            cell={cell}
            index={index}
            readonly={readonly}
            store={store}
            activeCellId={activeCellId}
            ref={ref}
            isDragging={isDragging}
            handlerId={handlerId}
            tinkerBoxId={store.tinkerBoxConnection.tinkerBox?.id}
          />
          <CellDivider
            onClick={() => {
              if (readonly) {
                toast('To edit, copy this notebook');
              } else {
                store.addCell(index + 1);
              }
            }}
          />
        </>
      );
    } else if (cell.cell_type === 'markdown') {
      return (
        <>
          <MarkdownCell
            cell={cell}
            index={index}
            readonly={readonly}
            store={store}
            activeCellId={activeCellId}
            ref={ref}
            isDragging={isDragging}
            handlerId={handlerId}
          />
          <CellDivider
            onClick={() => {
              if (readonly) {
                toast('To edit, copy this notebook');
              } else {
                store.addCell(index + 1);
              }
            }}
          />
        </>
      );
    }
    return <div>Unknown cell type: {JSON.stringify(cell)}</div>;
  }
);

interface CellOptionsProps {
  cellIndex: number;
  cellId: string;
  cellType: string;
  deleteCell: (id: number) => void;
  moveCell: (startIndex: number, endIndex: number) => void;
  changeCellType: (newType: MarkdownOrCode, cellId: string) => void;
}

export const CellOptions = ({
  cellIndex,
  cellId,
  cellType,
  deleteCell,
  moveCell,
  changeCellType
}: CellOptionsProps) => {
  return (
    <div className="absolute top-2 right-2 flex items-center text-neutral-500">
      <select
        className="bg-transparent"
        value={cellType}
        onChange={(e) =>
          changeCellType(e.target.value as MarkdownOrCode, cellId)
        }
      >
        {Object.values(MarkdownOrCode).map((type) => (
          <option value={type}>{type}</option>
        ))}
      </select>
      <button
        className="p-1 hover:text-black"
        onClick={() => moveCell(cellIndex, cellIndex - 1)}
      >
        <ArrowUpIcon size={16} />
      </button>
      <button
        className="p-1 hover:text-black"
        onClick={() => moveCell(cellIndex, cellIndex + 1)}
      >
        <ArrowDownIcon size={16} />
      </button>
      <button
        className="p-1 hover:text-black"
        onClick={() => deleteCell(cellIndex)}
      >
        <TrashIcon size={16} />
      </button>
    </div>
  );
};
